import { useMemo, useState } from "react";
import { ComboBoxItem, getTotalLength } from "@/utils/helper.util";
import type { CompanyAutocompleteEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyAutocompleteEntryDto";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";

import { SearchFilterItem } from "@/types/SearchFilterItem.type";
import { useCompanyAutocomplete } from "@/hooks/api/search/useCompanyAutocomplete";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Chip from "@/components/atoms/Chip";
import CompanyLogo from "@/components/molecules/company/CompanyLogo";
import FilterSectionItem from "@/components/molecules/filter/FilterSectionItem";
import FormCheckbox from "@/components/molecules/form/FormCheckbox";
import FormComboBox from "@/components/molecules/form/FormComboBox";

const CompanyFilter = ({ name }: SearchFilterItem) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const [companyIncludeQuery, setCompanyIncludeQuery] = useState("");
  const [companyExcludeQuery, setCompanyExcludeQuery] = useState("");

  const {
    query: companyInclude,
    setDebounceQuery: setCompanyInclude,
    isTyping: companyIncludeTyping,
  } = useDebounceQuery();

  const {
    query: companyExclude,
    setDebounceQuery: setCompanyExclude,
    isTyping: companyExcludeTyping,
  } = useDebounceQuery();

  const { data: includedCompanyData, isLoading: isLoadingCompanyInclude } = useCompanyAutocomplete(companyInclude);
  const { data: excludedCompanyData, isLoading: isLoadingCompanyExclude } = useCompanyAutocomplete(companyExclude);

  const companyIncludeValues: (CompanyAutocompleteEntryDto & ComboBoxItem)[] = watch("companyInclude");
  const companyExcludeValues: (CompanyAutocompleteEntryDto & ComboBoxItem)[] = watch("companyExclude");
  const showTerminated: boolean = watch("showTerminated");

  const showDisclaimer = useMemo(
    () =>
      !showTerminated &&
      companyIncludeValues.length > 0 &&
      companyIncludeValues.some((company) => !company.active && !company.isCustom),
    [showTerminated, includedCompanyData, companyIncludeValues],
  );

  return (
    <FilterSectionItem
      id={name}
      title={t("search.filter.company.title")}
      filterCount={getTotalLength([...companyExcludeValues, ...companyIncludeValues])}
      tooltip={t("search.filter.company.tooltip")}
    >
      <FormComboBox
        query={companyIncludeQuery}
        setQuery={(query) => {
          setCompanyIncludeQuery(query);
          setCompanyInclude(query);
        }}
        multiple
        showOptionsOnFocus
        name="companyInclude"
        placeholder={t("search.filter.company.placeholder")}
        options={includedCompanyData || []}
        isLoading={isLoadingCompanyInclude || companyIncludeTyping}
        renderOption={CompanyFilterOption}
        renderChip={(item, handleRemove) => (
          <Chip
            data-testid={`selected-option-${item.id}`}
            size={"xs"}
            color={item.active || item.isCustom ? "natural" : "warning"}
            key={item.id}
            label={item.name}
            className={"max-w-28"}
            onRemove={() => handleRemove(item)}
          />
        )}
      />

      {showDisclaimer && (
        <p className={"inline-flex gap-1 text-xs text-warning"}>
          <HiOutlineExclamationTriangle className={"size-4 shrink-0"} />
          {t("search.filter.company.terminatedDisclaimer")}
        </p>
      )}

      <FormComboBox
        query={companyExcludeQuery}
        setQuery={(query) => {
          setCompanyExcludeQuery(query);
          setCompanyExclude(query);
        }}
        multiple
        showOptionsOnFocus
        name="companyExclude"
        label={t("search.filter.company.exclude")}
        placeholder={t("search.filter.company.placeholder")}
        options={excludedCompanyData || []}
        isLoading={isLoadingCompanyExclude || companyExcludeTyping}
        renderOption={CompanyFilterOption}
      />

      <FormCheckbox name={"showTerminated"} label={t("search.filter.company.showTerminated")} />
    </FilterSectionItem>
  );
};

export default CompanyFilter;

export const CompanyFilterOption = (company: CompanyAutocompleteEntryDto) => {
  return (
    <div className={"flex items-center gap-2"}>
      <CompanyLogo url={company.logo} />
      <span className={"truncate"}>{company.name}</span>
    </div>
  );
};
